/**
 * members-tab.tsx — Tab "Nhân viên": vai trò, trạng thái, đăng nhập gần nhất (login_logs),
 * trực tuyến (user_presence), số phiên còn hạn (refresh_tokens).
 * Thao tác: khoá/mở (chặn khoá chủ sở hữu cuối), đặt lại mật khẩu, chuyển quyền chủ sở hữu,
 * đăng xuất mọi phiên, vào công ty (login-as), sửa thông tin (họ tên, email đăng nhập, SĐT, vai trò, bộ phận),
 * gỡ liên kết Google. Thao tác nhạy cảm bắt buộc nhập lý do.
 */
import { useState } from 'react'
import { KeyRound, Loader2, Lock, LogIn, LogOut, MoreHorizontal, Plus, Crown, Search, Unlock, Pencil, Unlink } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/misc'
import { Badge } from '@/components/ui/badge'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { DataTable, type Column } from '@/components/shared/data-table'
import { Pagination } from '@/components/shared/pagination'
import { ErrorState } from '@/components/shared/feedback'
import { cn } from '@/lib/utils'
import { platformApiError } from '@/lib/platform-client'
import { useAddOrgUser, useEnterCompany, useResetOrgUserPassword } from '@/hooks/use-platform'
import {
  useOrgMembers, useRevokeMemberSessions, useSetMemberStatus, useTransferOwner, useUnlinkMemberGoogle, type OrgMember,
} from '@/hooks/use-platform-org-manage'
import { displayVnPhone } from '@/pages/team/add-member-helpers'
import { EditMemberDialog } from './edit-member-dialog'
import { roleLabel } from '../org-status'
import { ReasonDialog } from './reason-dialog'
import { reasonError } from './reason'
import { PRESENCE, fmtDateTime } from './labels'

const PAGE_SIZE = 50
type Action = { kind: 'lock' | 'unlock' | 'transfer' | 'revoke' | 'unlink' | 'enter'; user: OrgMember }

export function MembersTab({ orgId }: { orgId: string }) {
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const { data, isLoading, isError } = useOrgMembers(orgId, { search: search.trim() || undefined, page, limit: PAGE_SIZE })
  const [addOpen, setAddOpen] = useState(false)
  const [resetUser, setResetUser] = useState<OrgMember | null>(null)
  const [editUser, setEditUser] = useState<OrgMember | null>(null)
  const [action, setAction] = useState<Action | null>(null)
  const enterCompany = useEnterCompany()
  const setStatus = useSetMemberStatus(orgId)
  const transfer = useTransferOwner(orgId)
  const revoke = useRevokeMemberSessions(orgId)
  const unlink = useUnlinkMemberGoogle(orgId)

  async function runAction(reason: string) {
    if (!action) return
    const { kind, user } = action
    try {
      if (kind === 'lock' || kind === 'unlock') {
        const r = (await setStatus.mutateAsync({ userId: user.id, isActive: kind === 'unlock', reason })) as { revokedSessions?: number }
        toast.success(kind === 'lock' ? `Đã khoá ${user.fullName}${r.revokedSessions ? ` · đăng xuất ${r.revokedSessions} phiên` : ''}` : `Đã mở khoá ${user.fullName}`)
      } else if (kind === 'transfer') {
        await transfer.mutateAsync({ userId: user.id, reason })
        toast.success(`${user.fullName} đã là chủ sở hữu`)
      } else if (kind === 'unlink') {
        await unlink.mutateAsync({ userId: user.id, reason: reason || undefined })
        toast.success(`Đã gỡ liên kết Google của ${user.fullName}`)
      } else if (kind === 'enter') {
        await enterCompany.mutateAsync({ orgId, userId: user.id, reason })
        toast.success(`Đang mở CRM với tài khoản ${user.fullName}...`)
        window.location.href = '/conversations'
        return
      } else {
        const r = await revoke.mutateAsync({ userId: user.id, reason })
        toast.success(`Đã đăng xuất ${r.revokedSessions} phiên của ${user.fullName}`)
      }
      setAction(null)
    } catch (err) {
      toast.error(platformApiError(err))
    }
  }

  const columns: Column<OrgMember>[] = [
    {
      key: 'user', header: 'Nhân viên', cell: (u) => (
        <div className="min-w-0">
          <p className="font-medium">{u.fullName}</p>
          <p className="text-xs text-muted-foreground">{u.email}</p>
          {(u.phone || u.teamName || u.googleLinked) && (
            <p className="text-xs text-muted-foreground">
              {[displayVnPhone(u.phone), u.teamName, u.googleLinked ? 'Đăng nhập Google' : ''].filter(Boolean).join(' · ')}
            </p>
          )}
        </div>
      ),
    },
    {
      key: 'role', header: 'Vai trò', cell: (u) => (
        <div className="flex flex-col items-start gap-0.5">
          <Badge variant={u.role === 'owner' ? 'default' : 'outline'}>{roleLabel(u.role)}</Badge>
          {u.roleName && u.roleName !== roleLabel(u.role) && <span className="text-xs text-muted-foreground">{u.roleName}</span>}
        </div>
      ),
    },
    {
      key: 'status', header: 'Trạng thái', cell: (u) =>
        u.isActive ? <Badge variant="success">Hoạt động</Badge>
          : <Badge variant="destructive">{u.lockedByPlatform ? 'Bị khoá bởi quản trị hệ thống' : 'Đã khoá'}</Badge>,
    },
    {
      key: 'presence', header: 'Trực tuyến', cell: (u) => {
        const p = PRESENCE[u.presence] ?? PRESENCE.offline
        return <span className="inline-flex items-center gap-1.5 text-sm"><span className={cn('h-2 w-2 rounded-full', p.dot)} />{p.text}</span>
      },
    },
    { key: 'login', header: 'Đăng nhập gần nhất', cell: (u) => fmtDateTime(u.lastLoginAt) },
    { key: 'sessions', header: 'Phiên', align: 'right', cell: (u) => u.activeSessions },
    { key: 'created', header: 'Ngày tạo', cell: (u) => fmtDateTime(u.createdAt) },
    {
      key: 'actions', header: '', align: 'right', cell: (u) => (
        <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
          <Button variant="ghost" size="sm" onClick={() => setAction({ kind: 'enter', user: u })} disabled={!u.isActive}>
            <LogIn className="h-4 w-4" /> Vào
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Thao tác"><MoreHorizontal className="h-4 w-4" /></Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => setEditUser(u)}><Pencil className="h-4 w-4" /> Sửa thông tin</DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setResetUser(u)}><KeyRound className="h-4 w-4" /> Đặt lại mật khẩu</DropdownMenuItem>
              {u.googleLinked && (
                <DropdownMenuItem onSelect={() => setAction({ kind: 'unlink', user: u })}><Unlink className="h-4 w-4" /> Gỡ liên kết Google</DropdownMenuItem>
              )}
              <DropdownMenuItem onSelect={() => setAction({ kind: 'revoke', user: u })}><LogOut className="h-4 w-4" /> Đăng xuất tất cả phiên</DropdownMenuItem>
              {u.role !== 'owner' && u.isActive && (
                <DropdownMenuItem onSelect={() => setAction({ kind: 'transfer', user: u })}><Crown className="h-4 w-4" /> Chuyển quyền chủ sở hữu</DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              {u.isActive ? (
                <DropdownMenuItem className="text-destructive" onSelect={() => setAction({ kind: 'lock', user: u })}><Lock className="h-4 w-4" /> Khoá tài khoản</DropdownMenuItem>
              ) : (
                <DropdownMenuItem onSelect={() => setAction({ kind: 'unlock', user: u })}><Unlock className="h-4 w-4" /> Mở khoá</DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    },
  ]

  const titles: Record<Action['kind'], { title: string; desc: string; label: string; destructive?: boolean }> = {
    lock: { title: 'Khoá tài khoản', desc: 'Nhân viên không đăng nhập được và bị đăng xuất khỏi mọi thiết bị. Không khoá được chủ sở hữu cuối cùng.', label: 'Khoá', destructive: true },
    unlock: { title: 'Mở khoá tài khoản', desc: 'Nhân viên đăng nhập lại được.', label: 'Mở khoá' },
    transfer: { title: 'Chuyển quyền chủ sở hữu', desc: 'Người này thành Chủ sở hữu; chủ sở hữu hiện tại chuyển thành Quản trị. Các bên được đăng xuất để nhận vai trò mới.', label: 'Chuyển quyền' },
    unlink: { title: 'Gỡ liên kết Google', desc: 'Nhân viên không đăng nhập bằng Google được nữa — dùng email + mật khẩu (đặt lại mật khẩu nếu chưa có). Có thể liên kết lại Google sau.', label: 'Gỡ liên kết' },
    revoke: { title: 'Đăng xuất tất cả phiên', desc: 'Thu hồi mọi phiên đăng nhập (refresh token) và ngắt kết nối realtime. Phiên đang mở còn dùng tối đa 30 phút tới khi token truy cập hết hạn.', label: 'Đăng xuất', destructive: true },
    enter: { title: 'Vào công ty', desc: 'Đăng nhập thay bằng tài khoản này — toàn quyền trên dữ liệu khách hàng. Lý do được ghi vào nhật ký quản trị Platform.', label: 'Vào công ty' },
  }
  const t = action ? titles[action.kind] : null
  const pending = setStatus.isPending || transfer.isPending || revoke.isPending || unlink.isPending || enterCompany.isPending

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="relative w-full max-w-xs">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input className="pl-8" placeholder="Tìm tên hoặc email" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1) }} />
        </div>
        <Button onClick={() => setAddOpen(true)}><Plus className="h-4 w-4" /> Thêm nhân viên</Button>
      </div>

      {isError ? <ErrorState message="Không tải được danh sách nhân viên." /> : (
        <DataTable columns={columns} rows={data?.items ?? []} loading={isLoading} rowKey={(u) => u.id} emptyTitle="Chưa có nhân viên" />
      )}
      {data && data.total > PAGE_SIZE && <Pagination page={page} limit={PAGE_SIZE} total={data.total} onPageChange={setPage} />}

      <AddUserDialog orgId={orgId} open={addOpen} onOpenChange={setAddOpen} />
      <ResetPasswordDialog orgId={orgId} user={resetUser} onClose={() => setResetUser(null)} />
      <EditMemberDialog orgId={orgId} member={editUser} onClose={() => setEditUser(null)} />
      {action && t && (
        <ReasonDialog
          open onOpenChange={(v) => !v && setAction(null)}
          title={`${t.title} — ${action.user.fullName}`} description={t.desc}
          confirmLabel={t.label} destructive={t.destructive} pending={pending} onConfirm={runAction}
          reasonRequired={action.kind !== 'unlink'}
        />
      )}
    </div>
  )
}

function AddUserDialog({
  orgId,
  open,
  onOpenChange,
}: {
  orgId: string
  open: boolean
  onOpenChange: (v: boolean) => void
}) {
  const addUser = useAddOrgUser(orgId)
  const [form, setForm] = useState({ fullName: '', email: '', password: '', role: 'member' })

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    try {
      await addUser.mutateAsync({
        fullName: form.fullName.trim(),
        email: form.email.trim(),
        password: form.password,
        role: form.role,
      })
      toast.success('Đã thêm người dùng')
      onOpenChange(false)
      setForm({ fullName: '', email: '', password: '', role: 'member' })
    } catch (err) {
      toast.error(platformApiError(err))
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Thêm người dùng</DialogTitle>
          <DialogDescription>Tạo tài khoản mới trong tổ chức này.</DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="u-name">Họ tên</Label>
            <Input id="u-name" value={form.fullName} onChange={(e) => setForm((f) => ({ ...f, fullName: e.target.value }))} required />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="u-email">Email</Label>
            <Input id="u-email" type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} required />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="u-password">Mật khẩu (≥ 8 ký tự)</Label>
              <Input id="u-password" type="password" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} required minLength={8} />
            </div>
            <div className="space-y-1.5">
              <Label>Vai trò</Label>
              <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="owner">Chủ sở hữu</SelectItem>
                  <SelectItem value="admin">Quản trị</SelectItem>
                  <SelectItem value="manager">Quản lý</SelectItem>
                  <SelectItem value="member">Nhân viên</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Hủy
            </Button>
            <Button type="submit" disabled={addUser.isPending}>
              {addUser.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Thêm
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function ResetPasswordDialog({
  orgId,
  user,
  onClose,
}: {
  orgId: string
  user: { id: string; fullName: string } | null
  onClose: () => void
}) {
  const reset = useResetOrgUserPassword(orgId)
  const [newPassword, setNewPassword] = useState('')
  const [reason, setReason] = useState('')
  // vbsec C9: đổi mật khẩu nhân viên bắt buộc lý do (ghi nhật ký Platform), khớp parseReason backend.
  const reasonMsg = reasonError(reason, true)

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!user || reasonMsg) return
    try {
      await reset.mutateAsync({ userId: user.id, newPassword, reason: reason.trim() })
      toast.success('Đã đổi mật khẩu')
      setNewPassword('')
      setReason('')
      onClose()
    } catch (err) {
      toast.error(platformApiError(err))
    }
  }

  return (
    <Dialog open={!!user} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Đổi mật khẩu</DialogTitle>
          <DialogDescription>{user ? `Đặt mật khẩu mới cho ${user.fullName}.` : ''}</DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="new-password">Mật khẩu mới (≥ 8 ký tự)</Label>
            <Input id="new-password" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required minLength={8} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="reset-reason">
              Lý do <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="reset-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              maxLength={500}
              placeholder="Ghi rõ lý do — lưu vào nhật ký quản trị Platform"
            />
            {reasonMsg && reason.trim().length > 0 && <p className="text-xs text-destructive">{reasonMsg}</p>}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Hủy
            </Button>
            <Button type="submit" disabled={reset.isPending || !!reasonMsg}>
              {reset.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Đổi mật khẩu
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
